import * as PIXI from 'pixi.js';
import { EnemyType } from '../types/GameTypes';
import { ENEMY_TYPE_DATA, calculateEnemyHP } from '../types/EnemyTypes';
import { WaveData } from '../types/GameTypes';

interface EnemyTypeInfo {
  type: EnemyType;
  count: number;
}

export class WaveInfoPanel {
  private container: PIXI.Container;
  private enemyBoxes: PIXI.Container[] = [];
  private waveData: WaveData | null = null;
  private waveNumber: number = 0;
  private expandedEnemyType: EnemyType | null = null; // Track by enemy type instead of box reference

  constructor() {
    this.container = new PIXI.Container();
    // Position on the right side for 1080p
    // Map is centered at x=640, width=640, so right edge is at 1280
    // Position panel to the right of the map, below log messages
    this.container.x = 1320;
    this.container.y = 280; // Below log messages area
    this.container.visible = false;
  }

  updateWave(waveData: WaveData, waveNumber: number): void {
    this.waveData = waveData;
    this.waveNumber = waveNumber;
    this.render();
  }

  private render(): void {
    // Clear existing boxes
    this.enemyBoxes.forEach(box => this.container.removeChild(box));
    this.enemyBoxes = [];
    this.container.removeChildren();

    if (!this.waveData) return;

    // Count enemy types
    const enemyCounts = new Map<EnemyType, number>();
    this.waveData.enemyTypes.forEach(type => {
      enemyCounts.set(type, (enemyCounts.get(type) || 0) + 1);
    });

    // Convert to array and sort
    const enemyInfos: EnemyTypeInfo[] = Array.from(enemyCounts.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => a.type.localeCompare(b.type));

    // Create boxes for each enemy type
    let yOffset = 10;
    const boxWidth = 200;
    const collapsedHeight = 30;
    const expandedHeight = 180;
    const boxSpacing = 10;

    enemyInfos.forEach((enemyInfo, _index) => {
      const isExpanded = this.expandedEnemyType === enemyInfo.type;
      const box = this.createEnemyBox(enemyInfo, boxWidth, isExpanded ? expandedHeight : collapsedHeight, this.waveNumber, isExpanded);
      box.x = 0;
      box.y = yOffset;
      box.eventMode = 'static';
      box.cursor = 'pointer';
      box.on('pointerdown', () => {
        // Toggle expansion - close if clicking the same type, open new one if different
        if (this.expandedEnemyType === enemyInfo.type) {
          this.expandedEnemyType = null;
        } else {
          this.expandedEnemyType = enemyInfo.type;
        }
        this.render(); // Re-render to update all boxes
      });
      this.container.addChild(box);
      this.enemyBoxes.push(box);
      yOffset += (isExpanded ? expandedHeight : collapsedHeight) + boxSpacing;
    });

    // Show panel if there are enemies
    this.container.visible = enemyInfos.length > 0;
  }

  private createEnemyBox(enemyInfo: EnemyTypeInfo, width: number, height: number, waveNumber: number, isExpanded: boolean): PIXI.Container {
    const box = new PIXI.Container();
    const data = ENEMY_TYPE_DATA[enemyInfo.type];
    const hp = calculateEnemyHP(enemyInfo.type, waveNumber, false);

    // Background
    const bg = new PIXI.Graphics();
    bg.beginFill(isExpanded ? 0x333333 : 0x222222, 0.8);
    bg.lineStyle(2, isExpanded ? 0x00ff00 : 0x444444); // Green border when expanded
    bg.drawRoundedRect(0, 0, width, height, 5);
    bg.endFill();
    box.addChild(bg);

    // Enemy name (capitalized)
    const nameText = new PIXI.Text(
      `${enemyInfo.type.charAt(0).toUpperCase() + enemyInfo.type.slice(1)} x${enemyInfo.count}`,
      {
        fontFamily: 'Courier New',
        fontSize: 18,
        fill: 0xffffff,
        fontWeight: 'bold',
      }
    );
    nameText.x = 5;
    nameText.y = 5;
    box.addChild(nameText);

    // Only show details if expanded
    if (isExpanded) {
      // Type info
      let typeInfo = '';
      if (data.isFlying) typeInfo = 'Flying';
      else if (data.isGhost) typeInfo = 'Ghost';
      else typeInfo = 'Ground';
      
      const typeText = new PIXI.Text(`Type: ${typeInfo}`, {
        fontFamily: 'Courier New',
        fontSize: 13,
        fill: 0xcccccc,
      });
      typeText.x = 5;
      typeText.y = 25;
      box.addChild(typeText);

      // HP
      const hpText = new PIXI.Text(`HP: ${hp.toLocaleString()}`, {
        fontFamily: 'Courier New',
        fontSize: 13,
        fill: 0xcccccc,
      });
      hpText.x = 5;
      hpText.y = 40;
      box.addChild(hpText);

      // Speed
      const speedText = new PIXI.Text(`Speed: ${data.baseSpeed.toFixed(1)}x`, {
        fontFamily: 'Courier New',
        fontSize: 13,
        fill: 0xcccccc,
      });
      speedText.x = 5;
      speedText.y = 55;
      box.addChild(speedText);

      // Resistances
      let resistanceText = 'Resist: ';
      if (data.physicalResistance > 0) {
        resistanceText += `${Math.round(data.physicalResistance * 100)}% Phys`;
        if (data.elementalVulnerability !== 1.0) {
          resistanceText += `, ${data.elementalVulnerability > 1 ? '+' : ''}${Math.round((data.elementalVulnerability - 1) * 100)}% Elem`;
        }
      } else if (data.elementalVulnerability !== 1.0) {
        resistanceText += `${data.elementalVulnerability > 1 ? '+' : ''}${Math.round((data.elementalVulnerability - 1) * 100)}% Elem`;
      } else {
        resistanceText += 'None';
      }
      
      const resText = new PIXI.Text(resistanceText, {
        fontFamily: 'Courier New',
        fontSize: 12,
        fill: 0xaaaaaa,
      });
      resText.x = 5;
      resText.y = 70;
      box.addChild(resText);

      // Description
      const description = this.getEnemyDescription(enemyInfo.type);
      const descText = new PIXI.Text(description, {
        fontFamily: 'Courier New',
        fontSize: 12,
        fill: 0x999999,
        wordWrap: true,
        wordWrapWidth: width - 10,
      });
      descText.x = 5;
      descText.y = 85;
      box.addChild(descText);
    }

    return box;
  }

  private getEnemyDescription(type: EnemyType): string {
    switch (type) {
      case 'ground':
        return 'Basic enemy unit';
      case 'runner':
        return 'Fast but weak';
      case 'armored':
        return 'High HP, physical resist';
      case 'splitter':
        return 'Splits on death';
      case 'healer':
        return 'Heals nearby enemies';
      case 'ghost':
        return '80% phys resist, ignores walls';
      case 'flying':
        return 'Immune to phys, ignores walls';
      case 'elite':
        return 'Enhanced stats';
      case 'miniboss':
        return 'Powerful mini-boss';
      case 'boss':
        return 'Final boss of wave';
      default:
        return 'Unknown enemy';
    }
  }

  getContainer(): PIXI.Container {
    return this.container;
  }

  hide(): void {
    this.container.visible = false;
  }

  show(): void {
    if (this.waveData) {
      this.container.visible = true;
    }
  }
}

